/**
 * @file transportLock.js
 * @version 1.1.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-13
 * @modified 2026-10-26
 * @description WHO may change a line's haulier, driver and vehicle, WHEN, and
 *              WHERE — one rule for every path (Cam, 13 Oct 2026: "locked to
 *              transport planning only until the day it is delivered"; for a
 *              self-billed PO "until the day following the delivery date
 *              (view only on the PO up to that point)").
 *
 *   A line ON A PLANNING BOARD — an order line bought on a PO-D or supplied
 *   off our stock, on an order the boards show (not Third-Party, not Direct
 *   Delivery), or a self-billed PO we carry:
 *
 *     - up to and INCLUDING its delivery date (London): phase 'board' — set
 *       ONLY on the planning board (the board's own rules: the day lock, the
 *       transport_planning permission). Every other path — the PO editor,
 *       Add / Update Supplied Qty — refuses a change, saying "Set on the
 *       Transport Planning board until <date>", and shows the cells read-only;
 *     - from the day AFTER: phase 'correction' — a record of who carried it.
 *       Correctable outside the board too, by holders of transport_planning,
 *       WITHOUT the unlock password (that guards the day's plan on the
 *       board); every such correction also corrects the board's record of
 *       that day (its snapshot) and goes in Plan History.
 *
 *   Not on a board — phase 'free': a Third-Party or Direct Delivery order or
 *   PO-D (someone else carries it), a self-billed PO someone else carries, a
 *   PO with no order that is not self-billed. Each path's own rules, as
 *   before.
 *
 *   Customer Collection (both sides) — phase 'none': no haulier, driver or
 *   vehicle at all, from anywhere.
 *
 *   1.1.0 (2026-10-26, Cam): a Third-Party Delivery order planned on the
 *   board — a PO-D, its third-party haulier supplier linked to a transport
 *   haulier (server utils/thirdPartyBoard) — is ON A BOARD like any other:
 *   orderOnBoard({ …, thirdPartyOnBoard: true }), callers passing what the
 *   database says (`third_party_on_board`). Its PO-D lines follow the board
 *   rule; its stock lines (the PO-H collects them) stay free.
 *
 *   The server's utils/transportLock.js is GENERATED from this file by
 *   scripts/sync-shared-utils.js — edit this one and re-run it.
 */

import { isCustomerCollection } from './deliveryTerms';
import { selfBilledCarrier } from './selfBilledPo';

export const TRANSPORT_PHASE_NONE = 'none';
export const TRANSPORT_PHASE_FREE = 'free';
export const TRANSPORT_PHASE_BOARD = 'board';
export const TRANSPORT_PHASE_CORRECTION = 'correction';

/** The paths transport can be changed on. */
export const PATH_BOARD = 'board';
export const PATH_PO_EDITOR = 'PO editor';
export const PATH_SUPPLIED_QTY = 'Add Supplied Qty';

export const CORRECTION_NEEDS_PERMISSION =
  'Correcting who carried it needs the Edit Transport Planning permission';
export const NO_TRANSPORT_REASON =
  'Customer Collection — the customer collects, so there is no haulier, driver or vehicle of ours';

const DAY = /^\d{4}-\d{2}-\d{2}$/;
const dayOf = (d) => {
  const s = d == null ? '' : String(d).slice(0, 10);
  return DAY.test(s) ? s : null;
};

/** 'YYYY-MM-DD' plus n days (calendar arithmetic, no time zone involved). */
export function addDays(date, n) {
  const d = dayOf(date);
  if (!d) return null;
  const [y, m, day] = d.split('-').map(Number);
  const t = new Date(Date.UTC(y, m - 1, day + n));
  return t.toISOString().slice(0, 10);
}

/** 'DD/MM/YYYY' for a 'YYYY-MM-DD'. */
export function ukDay(date) {
  const d = dayOf(date);
  if (!d) return '';
  const [y, m, day] = d.split('-');
  return `${day}/${m}/${y}`;
}

/** Today in the office (London), 'YYYY-MM-DD'. */
export function londonToday(now = new Date()) {
  return now.toLocaleDateString('en-CA', { timeZone: 'Europe/London' });
}

/** The latest of some delivery dates (a PO and its order's), or null. */
export function latestDay(dates) {
  const days = (Array.isArray(dates) ? dates : [dates]).map(dayOf).filter(Boolean).sort();
  return days.length ? days[days.length - 1] : null;
}

/**
 * The rule for one line now.
 * @param {object} p
 * @param {boolean} p.onBoard       the line is on a planning board
 * @param {string}  p.deliveryDate  its delivery date (the board's day), 'YYYY-MM-DD'
 * @param {string}  p.today         London today, 'YYYY-MM-DD'
 * @param {boolean} [p.noTransport] Customer Collection — none at all
 * @returns {{ phase, boardUntil: string|null, correctFrom: string|null, reason: string|null }}
 */
export function transportRule({ onBoard = false, deliveryDate = null, today, noTransport = false } = {}) {
  if (noTransport) return { phase: TRANSPORT_PHASE_NONE, boardUntil: null, correctFrom: null, reason: NO_TRANSPORT_REASON };
  const date = dayOf(deliveryDate);
  if (!onBoard || !date) return { phase: TRANSPORT_PHASE_FREE, boardUntil: null, correctFrom: null, reason: null };
  const correctFrom = addDays(date, 1);
  if (date >= String(today || '')) {
    return {
      phase: TRANSPORT_PHASE_BOARD,
      boardUntil: date,
      correctFrom,
      reason: `Set on the Transport Planning board until ${ukDay(date)} — it can be corrected here from ${ukDay(correctFrom)}`
    };
  }
  return { phase: TRANSPORT_PHASE_CORRECTION, boardUntil: date, correctFrom, reason: null };
}

/**
 * Why `path` may not change this line's transport now, or null.
 * The board's own day lock and permission are the board's (routes/transport).
 * @param {object} rule      transportRule(...)
 * @param {object} p
 * @param {string} p.path    PATH_BOARD | PATH_PO_EDITOR | PATH_SUPPLIED_QTY
 * @param {boolean} [p.canPlan]   holds transport_planning
 */
export function transportChangeRefusal(rule, { path, canPlan = false } = {}) {
  if (!rule) return null;
  if (rule.phase === TRANSPORT_PHASE_NONE) return rule.reason;
  if (path === PATH_BOARD) return null;
  if (rule.phase === TRANSPORT_PHASE_BOARD) return rule.reason;
  if (rule.phase === TRANSPORT_PHASE_CORRECTION && !canPlan) return CORRECTION_NEEDS_PERMISSION;
  return null;
}

/** Does a change on `path` also correct the board's record of the day (its snapshot, Plan History)? */
export const correctsBoardRecord = (rule, path) => !!rule && rule.phase === TRANSPORT_PHASE_CORRECTION && path !== PATH_BOARD;

/** The three cells, compared as the save compares them (blank = none). */
export const TRANSPORT_FIELDS = ['haulier', 'driver', 'vehicle'];
const cell = (v) => {
  const s = v == null ? '' : String(v).trim();
  return s === 'Customer Collection' ? '' : s;
};

/** Which of haulier / driver / vehicle differ between two lines (blank = none). */
export function transportChanges(was, now) {
  return TRANSPORT_FIELDS.filter(f => cell(was && was[f]) !== cell(now && now[f]));
}

/**
 * Is an order on the boards? Not Direct Delivery (the supplier delivers), and
 * not Third-Party Delivery (a PO-H carries it) — unless it is planned there:
 * a PO-D and its third-party haulier linked to a transport haulier
 * (`thirdPartyOnBoard`, 26 Oct 2026). The planning query's own test.
 */
export function orderOnBoard({ supplierTerms = null, ourTerms = null, thirdPartyOnBoard = false } = {}) {
  if (String(supplierTerms || '') === 'Direct Delivery') return false;
  if (String(ourTerms || '') === 'Third-Party Delivery') return !!thirdPartyOnBoard;
  return true;
}

/**
 * The rule for a purchase order's lines (the PO editor).
 *   - A self-billed PO: on the board on its own date when we carry it;
 *     someone else carrying it (Direct Delivery, third-party) is free;
 *     Customer Collection on both its sides has none.
 *   - A PO for customer orders: on a board when any of its orders is (not
 *     Direct Delivery; not Third-Party unless `third_party_on_board`), on the later of the PO's date and
 *     those orders' dates; every order Customer Collection — none.
 *   - Any other PO (no order, not self-billed): free.
 * @param {object} p
 * @param {boolean} [p.selfBilled]
 * @param {string}  [p.supplierTerms], [p.ourTerms]  a self-billed PO's own
 * @param {Array}   [p.linkedOrders]  [{ supplier_delivery_terms, excel_agri_delivery_terms, delivery_date }]
 * @param {string|Array} [p.poDeliveryDate]  the PO's date (or its stored and new dates)
 * @param {string}  p.today
 */
export function poTransportRule({ selfBilled = false, supplierTerms = null, ourTerms = null, linkedOrders = [], poDeliveryDate = null, today } = {}) {
  const poDates = Array.isArray(poDeliveryDate) ? poDeliveryDate : [poDeliveryDate];
  if (selfBilled) {
    const noTransport = isCustomerCollection(supplierTerms, ourTerms);
    const carried = !noTransport && !!selfBilledCarrier(supplierTerms, ourTerms);
    return transportRule({ onBoard: !carried, deliveryDate: latestDay(poDates), today, noTransport });
  }
  const orders = Array.isArray(linkedOrders) ? linkedOrders : [];
  if (!orders.length) return transportRule({ onBoard: false, today });
  const noTransport = orders.every(o => isCustomerCollection(o.supplier_delivery_terms, o.excel_agri_delivery_terms));
  const boardOrders = orders.filter(o => orderOnBoard({
    supplierTerms: o.supplier_delivery_terms, ourTerms: o.excel_agri_delivery_terms,
    thirdPartyOnBoard: !!o.third_party_on_board
  }));
  return transportRule({
    onBoard: boardOrders.length > 0,
    deliveryDate: latestDay([...poDates, ...boardOrders.map(o => o.delivery_date)]),
    today,
    noTransport
  });
}
